import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Plus, X, Mail, Clock, Image, Users as UsersIcon, Send, RefreshCw, CheckCircle2 } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [newOp, setNewOp] = useState("");
  const [tg, setTg] = useState({ chats: [], configured: false });
  const [tgBusy, setTgBusy] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/telegram/chats").then((r) => setTg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const tgRefresh = async () => {
    setTgBusy(true);
    try {
      const r = await api.post("/admin/telegram/refresh");
      setTg((t) => ({ ...t, chats: r.data.chats }));
      toast.success(r.data.chats.length ? `${r.data.chats.length} destinatario/i trovati` : "Nessun destinatario: avvia prima il bot con /start");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setTgBusy(false);
  };

  const tgTest = async () => {
    setTgBusy(true);
    try {
      await api.post("/admin/telegram/test");
      toast.success("Messaggio di test inviato su Telegram");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    setTgBusy(false);
  };

  const save = async (patch) => {
    const r = await api.patch("/admin/settings", patch);
    setSettings(r.data);
    toast.success("Impostazioni salvate");
  };

  const setRole = async (uid, role) => {
    await api.patch(`/admin/users/${uid}/role`, { role });
    toast.success("Ruolo aggiornato");
    load();
  };

  if (!settings) return <div className="text-gray-400">Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Impostazioni</h1>

      {/* Logo */}
      <Card title="Logo" icon={Image}>
        <div className="flex items-center gap-4">
          <div className="bg-[#111111] rounded-xl p-3"><img src={settings.logo_url} alt="logo" className="h-8" /></div>
          <div className="flex-1">
            <Label className="mb-1.5 block text-xs text-gray-500">URL logo (modificabile senza codice)</Label>
            <Input data-testid="logo-url-input" defaultValue={settings.logo_url} onBlur={(e) => e.target.value !== settings.logo_url && save({ logo_url: e.target.value })} className="rounded-xl" />
          </div>
        </div>
      </Card>

      {/* Time slots */}
      <Card title="Fasce orarie disponibili" icon={Clock}>
        <div className="flex flex-wrap gap-2 mb-4">
          {settings.time_slots.map((s) => (
            <span key={s} data-testid={`slot-${s}`} className="inline-flex items-center gap-1 bg-gray-100 rounded-full pl-3 pr-1.5 py-1 text-sm">
              {s}
              <button onClick={() => save({ time_slots: settings.time_slots.filter((x) => x !== s) })} className="hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input placeholder="es. 19:00-20:00" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} className="rounded-xl" data-testid="new-slot-input" />
          <Button onClick={() => { if (newSlot) { save({ time_slots: [...settings.time_slots, newSlot] }); setNewSlot(""); } }} className="rounded-xl bg-[#111111] text-white" data-testid="add-slot-btn"><Plus className="w-4 h-4" /></Button>
        </div>
      </Card>

      {/* Email */}
      <Card title="Notifiche email" icon={Mail}>
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="font-medium text-sm">Email di conferma al cliente</p>
            <p className="text-xs text-gray-500">Invia automaticamente una email al cliente segnalato</p>
          </div>
          <Switch checked={settings.client_email_enabled} onCheckedChange={(v) => save({ client_email_enabled: v })} data-testid="client-email-switch" className="data-[state=checked]:bg-[#FFCC00]" />
        </div>
        <div className="mt-3">
          <Label className="mb-1.5 block text-xs text-gray-500">Email operatori (ricevono le notifiche)</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {(settings.operator_emails || []).map((e) => (
              <span key={e} className="inline-flex items-center gap-1 bg-gray-100 rounded-full pl-3 pr-1.5 py-1 text-sm">
                {e}
                <button onClick={() => save({ operator_emails: settings.operator_emails.filter((x) => x !== e) })} className="hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input placeholder="email@dopopay.it" value={newOp} onChange={(e) => setNewOp(e.target.value)} className="rounded-xl" data-testid="new-op-email" />
            <Button onClick={() => { if (newOp) { save({ operator_emails: [...(settings.operator_emails || []), newOp] }); setNewOp(""); } }} className="rounded-xl bg-[#111111] text-white"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      {/* Telegram */}
      <Card title="Notifiche Telegram (avvisi tecnici)" icon={Send}>
        <p className="text-sm text-gray-600 mb-3">
          Ricevi un avviso su Telegram quando viene rilevato un problema tecnico (es. email non funzionante). Funziona anche se l'email è ferma.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 mb-4">
          <b>Come attivarlo:</b> apri Telegram, cerca il tuo bot e premi <b>Avvia / /start</b>. Poi clicca "Aggiorna destinatari" qui sotto.
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button onClick={tgRefresh} disabled={tgBusy} data-testid="tg-refresh-btn" variant="outline" className="rounded-xl border-gray-300">
            <RefreshCw className={`w-4 h-4 mr-2 ${tgBusy ? "animate-spin" : ""}`} /> Aggiorna destinatari
          </Button>
          <Button onClick={tgTest} disabled={tgBusy || tg.chats.length === 0} data-testid="tg-test-btn" className="rounded-xl bg-[#111111] text-white">
            <Send className="w-4 h-4 mr-2" /> Invia messaggio di test
          </Button>
        </div>
        {tg.chats.length === 0 ? (
          <p className="text-sm text-gray-400">Nessun destinatario registrato. Avvia il bot con /start e aggiorna.</p>
        ) : (
          <div className="space-y-2">
            {tg.chats.map((c) => (
              <div key={c.chat_id} data-testid={`tg-chat-${c.chat_id}`} className="flex items-center gap-2 border border-gray-100 rounded-xl px-4 py-2.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-medium">{c.name}</span>
                {c.username && <span className="text-gray-400">@{c.username}</span>}
                <span className="text-gray-300 ml-auto font-mono text-xs">{c.chat_id}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Users / roles */}
      <Card title="Utenti e ruoli" icon={UsersIcon}>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between border border-gray-100 rounded-xl px-4 py-2.5">
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{u.name}</p>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} data-testid={`role-select-${u.email}`}
                className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                <option value="admin">Amministratore</option>
                <option value="operator">Operatore</option>
                <option value="agent">Agente</option>
              </select>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">Integrazioni future predisposte: WhatsApp Business API, SMS, CRM.</p>
      </Card>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
      <h2 className="font-display font-semibold mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#E6B800]" />}{title}</h2>
      {children}
    </div>
  );
}
